/**
 * arrocha-banner-vigencia.js
 * Farmacias Arrocha — Vigilancia client-side de vigencia de banners (slideshow)
 *
 * Propósito:
 *   Complementar la validación server-side (Liquid) del módulo de vigencia de
 *   slides. El Liquid decide si un slide se renderiza al CARGAR la página, pero
 *   no puede ocultar un slide que expira mientras el usuario ya tiene la página
 *   abierta (por ejemplo, de un día para otro). Este script vigila en tiempo
 *   real la fecha de despublicación de los slides visibles y, cuando uno expira,
 *   recarga la página UNA SOLA VEZ para que el Liquid regenere el slideshow ya
 *   sin ese slide.
 *
 * Por qué es necesario:
 *   En promociones de farmacia/retail, mostrar un banner vencido (aunque sea por
 *   una sesión que quedó abierta) puede constituir publicidad engañosa. Este
 *   script garantiza que un banner expirado no permanezca visible una vez que
 *   pasa su fecha de despublicación, sin depender de que el usuario recargue
 *   manualmente.
 *
 * Blindaje anti-loop (CRÍTICO):
 *   La regla es UN RELOAD POR BANNER, NUNCA MÁS. Cada slide tiene un block.id
 *   único de Shopify. Cuando un banner expira y provoca un reload, su id se
 *   registra en sessionStorage ANTES de recargar. Tras el reload, aunque el
 *   caché de Shopify llegara a servir momentáneamente el slide vencido, su id
 *   ya está marcado y el script lo ignora. Esto hace imposible un loop infinito
 *   de recargas: cada banner solo puede disparar un reload por sesión.
 *
 * Estrategia:
 *   - No usa polling agresivo (no checa cada segundo). Programa un único
 *     setTimeout para el momento exacto de expiración del slide más próximo.
 *   - Aplica un margen de gracia de GRACE_PERIOD_MS después de la hora de
 *     expiración antes de recargar, para dar tiempo al caché de Shopify a
 *     reflejar el cambio server-side.
 *   - Solo vigila la EXPIRACIÓN de slides (schedule_end). Los slides que inician
 *     su vigencia aparecen en la siguiente carga natural de la página; el riesgo
 *     legal es mostrar algo vencido, no la ausencia de algo que apenas inicia.
 *
 * Dependencias:
 *   - Los slides deben emitir los atributos data-block-id y data-schedule-end
 *     (configurado en section.slideshow.slide.liquid).
 *   - La fecha en data-schedule-end debe venir en hora de Panamá (UTC-5),
 *     consistente con la validación Liquid.
 *
 * Autor: Informatica EasyGo
 * Versión: 1.0.0
 * Fecha: Junio 2026
 */
 
(function () {
  'use strict';
 
  /* ═══════════════════════════════════════════════════════════════════════
     CONFIGURACIÓN
     ═══════════════════════════════════════════════════════════════════════ */
 
  /**
   * Flag de debug.
   * Cuando es true, imprime en consola el seguimiento de la vigilancia
   * (slides detectados, tiempos programados, reloads). En produccion: false.
   */
  var DEBUG = true;
 
  /**
   * Margen de gracia en milisegundos. El reload se dispara este tiempo DESPUES
   * de la hora exacta de expiracion, para dar tiempo al cache de Shopify a
   * servir la pagina ya regenerada sin el slide vencido.
   */
  var GRACE_PERIOD_MS = 70000; // 70  segundos
 
  /**
   * Offset de zona horaria de Panama respecto a UTC, en minutos.
   * Panama es UTC-5 fijo todo el año (no usa horario de verano).
   * Se usa para interpretar las fechas de los slides en hora de Panama,
   * independientemente de la zona horaria del navegador del visitante.
   */
  var PANAMA_UTC_OFFSET_MINUTES = -300; // UTC-5 = -300 minutos
 
  /**
   * Clave de sessionStorage donde se guarda la lista de block.id de banners
   * que ya provocaron un reload en esta sesion. Garantiza un reload por banner.
   */
  var RELOADED_BANNERS_KEY = 'arrocha_banners_reloaded';
 
  /* ═══════════════════════════════════════════════════════════════════════
     UTILIDADES
     ═══════════════════════════════════════════════════════════════════════ */
 
  /**
   * log
   * Wrapper de console.log que solo imprime si DEBUG === true.
   */
  function log() {
    if (DEBUG) {
      console.log.apply(console, arguments);
    }
  }
 
  /**
   * parsePanamaDate
   *
   * Convierte una cadena "AAAA-MM-DD HH:MM" (interpretada como hora de Panama)
   * a un objeto Date con el instante UTC equivalente. Esto permite comparar
   * contra el reloj real del visitante sin importar su zona horaria local.
   *
   * @param {string} raw - Fecha en formato "AAAA-MM-DD HH:MM"
   * @returns {Date|null} Date válido, o null si el formato es inválido
   */
  function parsePanamaDate(raw) {
    if (!raw) return null;
    var trimmed = String(raw).trim();
    // Captura: año, mes, día, hora, minuto
    var match = trimmed.match(/^(\d{4})-(\d{2})-(\d{2})[ T](\d{2}):(\d{2})$/);
    if (!match) return null;
 
    var year = parseInt(match[1], 10);
    var month = parseInt(match[2], 10) - 1; // JS: meses 0-11
    var day = parseInt(match[3], 10);
    var hour = parseInt(match[4], 10);
    var minute = parseInt(match[5], 10);
 
    // Construye el instante en UTC asumiendo que la hora dada es de Panama.
    // Panama (UTC-5) significa que, por ejemplo, las 18:00 en Panama equivalen
    // a las 23:00 UTC. Por eso se RESTA el offset (que es negativo) => suma 5h.
    var utcMillis = Date.UTC(year, month, day, hour, minute) - (PANAMA_UTC_OFFSET_MINUTES * 60000);
    var d = new Date(utcMillis);
    if (isNaN(d.getTime())) return null;
    return d;
  }
 
  /**
   * getReloadedBanners
   * Lee de sessionStorage la lista de block.id que ya provocaron reload.
   * @returns {string[]} Array de ids (vacío si no hay registro o hay error)
   */
  function getReloadedBanners() {
    try {
      var raw = sessionStorage.getItem(RELOADED_BANNERS_KEY);
      if (!raw) return [];
      var parsed = JSON.parse(raw);
      if (Object.prototype.toString.call(parsed) === '[object Array]') {
        return parsed;
      }
      return [];
    } catch (e) {
      return [];
    }
  }
 
  /**
   * markBannerReloaded
   * Agrega un block.id a la lista de banners que ya provocaron reload.
   * @param {string} blockId
   */
  function markBannerReloaded(blockId) {
    try {
      var list = getReloadedBanners();
      if (list.indexOf(blockId) === -1) {
        list.push(blockId);
        sessionStorage.setItem(RELOADED_BANNERS_KEY, JSON.stringify(list));
      }
    } catch (e) {
      // Si sessionStorage falla, no podemos garantizar el anti-loop; por
      // seguridad NO recargamos (mejor un banner vencido un momento que un loop).
      log('[Arrocha Vigencia] No se pudo escribir en sessionStorage:', e);
    }
  }
 
  /* ═══════════════════════════════════════════════════════════════════════
     VIGILANCIA
     ═══════════════════════════════════════════════════════════════════════ */
 
  /**
   * scheduleExpirationCheck
   *
   * Recorre los slides visibles, identifica el que expira más próximo (entre
   * los que aún no han provocado reload) y programa un único setTimeout para
   * recargar la página cuando ese slide expire (más el margen de gracia).
   */
  function scheduleExpirationCheck() {
    var slides = document.querySelectorAll('[data-slideshow-slide][data-schedule-end]');
    if (!slides || slides.length === 0) {
      log('[Arrocha Vigencia] No hay slides con fecha de expiracion.');
      return;
    }
 
    var reloadedBanners = getReloadedBanners();
    var now = new Date();
    var nextExpiration = null; // { blockId, expiresAt (Date) }
 
    for (var i = 0; i < slides.length; i++) {
      var slide = slides[i];
      var blockId = slide.getAttribute('data-block-id') || '';
      var endRaw = slide.getAttribute('data-schedule-end') || '';
 
      if (!blockId || !endRaw) continue;
 
      
      
      // Clave unica = block.id + fecha de expiracion. Asi, si el mismo banner
      // se reutiliza con una fecha nueva, se considera distinto y vuelve a vigilarse.
      var bannerKey = blockId + '|' + endRaw;

      // Si esta combinacion banner+fecha ya provoco un reload, la ignoramos.
      if (reloadedBanners.indexOf(bannerKey) !== -1) {
        log('[Arrocha Vigencia] Banner', bannerKey, 'ya provoco reload antes. Ignorado.');
        continue;
      }

      var expiresAt = parsePanamaDate(endRaw);
      if (!expiresAt) {
        log('[Arrocha Vigencia] Fecha invalida en banner', blockId, ':', endRaw);
        continue;
      }
 


      // Solo nos interesan expiraciones futuras (las pasadas ya las filtro Liquid).
      if (expiresAt.getTime() <= now.getTime()) {
        log('[Arrocha Vigencia] Banner', blockId, 'ya expirado al cargar (deberia haberlo filtrado Liquid).');
        continue;
      }
 
      if (nextExpiration === null || expiresAt.getTime() < nextExpiration.expiresAt.getTime()) {
        nextExpiration = { blockId: blockId, bannerKey: bannerKey, expiresAt: expiresAt };
      }
    }
 
    if (nextExpiration === null) {
      log('[Arrocha Vigencia] No hay expiraciones futuras pendientes que vigilar.');
      return;
    }
 
    var msUntilReload = (nextExpiration.expiresAt.getTime() - now.getTime()) + GRACE_PERIOD_MS;
 
    log(
      '[Arrocha Vigencia] Proximo banner a expirar:',
      nextExpiration.blockId,
      '— en',
      Math.round(msUntilReload / 1000),
      'segundos (incluye margen de gracia).'
    );
 
    setTimeout(function () {
      // Registrar ANTES de recargar para garantizar un reload por banner.
      markBannerReloaded(nextExpiration.bannerKey);
      log('[Arrocha Vigencia] Banner', nextExpiration.blockId, 'expirado. Recargando una vez.');
      // Recarga con cache-buster para forzar version fresca del servidor
      // (un reload normal puede traer la pagina cacheada con el banner aun visible).
      var url = new URL(window.location.href);
      url.searchParams.set('_vig', Date.now());
      window.location.href = url.toString();
    }, msUntilReload);
  }
 
  /* ═══════════════════════════════════════════════════════════════════════
     INICIALIZACIÓN
     ═══════════════════════════════════════════════════════════════════════ */
 
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', scheduleExpirationCheck);
  } else {
    scheduleExpirationCheck();
  }
 
  log('[Arrocha Vigencia] Vigilancia de vigencia de banners activa.');
 
})();